import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ShipmentCard } from '@/components/features/shipments/ShipmentCard';
import { ShipmentListItem } from '@/types/shipment.types';
import { ROUTES } from '@/lib/constants';
import { formatDate } from '@/lib/formatters';
import { PackageOpen, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { api } from '@/lib/api';

interface TenantShipmentsListProps {
  tenantId: string;
}

interface FilterValues {
  search: string;
  status_code: string;
}

const initialFilters: FilterValues = {
  search: '',
  status_code: '',
};

interface LookupOption {
  value: string;
  label: string;
}

export function TenantShipmentsList({ tenantId }: TenantShipmentsListProps) {
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const pagination = usePagination();
  
  const [shipments, setShipments] = useState<ShipmentListItem[]>([]);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [statusOptions, setStatusOptions] = useState<LookupOption[]>([]);
  
  const debouncedSearch = useDebounce(filters.search, 300);

  useEffect(() => {
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    fetchShipments();
  }, [pagination.page, debouncedSearch, filters.status_code, tenantId]);

  const fetchStatusOptions = async () => {
    try {
      const response: any = await api.get('/api/v1/lookups/shipment-statuses?lang=ar');
      const items = response?.data?.items || response?.items || [];
      setStatusOptions(items.map((item: any) => ({
        value: String(item.value || ''),
        label: String(item.label || ''),
      })).filter((o: LookupOption) => o.value && o.label));
    } catch (error) {
      console.error('Failed to fetch status options:', error);
    }
  };

  const fetchShipments = async () => {
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: pagination.limit,
        tenant_id: tenantId,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status_code) params.status_code = filters.status_code;

      const response: any = await get('/api/v1/dashboard/shipments', params);

      setShipments(response?.list || []);
      pagination.updatePagination({
        total_pages: response?.meta?.totalPages || 0,
        total: response?.meta?.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch tenant shipments:', error);
    }
  };

  const handleShipmentClick = (shipment: ShipmentListItem) => {
    navigate(ROUTES.SHIPMENT_DETAILS(shipment.id));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    pagination.reset();
  };

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? '' : value,
    }));
  };

  const columns = [
    {
      header: 'رقم التتبع',
      accessor: 'tracking_number' as keyof ShipmentListItem,
      className: 'font-medium',
    },
    {
      header: 'الحالة',
      accessor: (row: ShipmentListItem) => <StatusBadge status={row.status} />,
    },
    {
      header: 'العنوان',
      accessor: (row: ShipmentListItem) => row.address,
    },
    {
      header: 'المستلم',
      accessor: (row: ShipmentListItem) => row.recipient_name,
    },
    {
      header: 'نوع الخدمة',
      accessor: (row: ShipmentListItem) => row.service_type,
    },
    {
      header: 'القيمة',
      accessor: (row: ShipmentListItem) => `${row.shipment_value}`,
    },
    {
      header: 'التاريخ',
      accessor: (row: ShipmentListItem) => formatDate(row.created_at),
      className: 'text-muted-foreground',
    },
  ];

  const hasActiveFilters = filters.search !== '' || filters.status_code !== '';

  if (loading && shipments.length === 0) {
    return <LoadingSkeleton type="table" rows={5} />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="بحث برقم التتبع أو الاسم..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pe-10"
          />
        </div>
        <div className="w-[180px]">
          <SearchableSelect
            options={statusOptions}
            value={filters.status_code}
            onValueChange={(v) => handleFilterChange('status_code', v)}
            placeholder="الحالة"
            searchPlaceholder="بحث..."
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            مسح الفلاتر
          </Button>
        )}
      </div>

      {/* Content */}
      {shipments.length === 0 ? (
        <EmptyState
          icon={<PackageOpen className="h-12 w-12" />}
          title="لا توجد شحنات"
          description="لم يتم العثور على شحنات لهذا الشريك"
          action={
            hasActiveFilters
              ? { label: 'مسح الفلاتر', onClick: handleClearFilters }
              : undefined
          }
        />
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <DataTable
              data={shipments}
              columns={columns}
              onRowClick={handleShipmentClick}
              showViewButton={true}
              pagination={{
                currentPage: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: pagination.goToPage,
              }}
              emptyMessage="لا توجد شحنات"
            />
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {shipments.map((shipment) => (
              <ShipmentCard
                key={shipment.id}
                shipment={shipment}
                onClick={() => handleShipmentClick(shipment)}
              />
            ))}
            
            {/* Mobile Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  صفحة {pagination.page} من {pagination.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => pagination.prevPage()}
                    disabled={!pagination.hasPrev}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                  >
                    السابق
                  </button>
                  <button
                    onClick={() => pagination.nextPage()}
                    disabled={!pagination.hasNext}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                  >
                    التالي
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
